import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../services/api';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';

export function useAgents() {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  // Get all agents
  const { data: agents, isLoading, error } = useQuery({
    queryKey: ['agents'],
    queryFn: agentsApi.getAgents,
    enabled: !authLoading && !!user,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (agents don't change often)
  });

  // Get single agent
  const getAgent = (agentType: string) => {
    return useQuery({
      queryKey: ['agents', agentType],
      queryFn: () => agentsApi.getAgent(agentType),
      enabled: !authLoading && !!user,
      staleTime: 24 * 60 * 60 * 1000, // 24 hours
    });
  };

  // Chat with agent mutation
  const chatMutation = useMutation({
    mutationFn: ({ agentType, message, conversationId, context, projectId }: {
      agentType: string;
      message: string;
      conversationId?: string;
      context?: Record<string, any>;
      projectId?: string;
    }) => agentsApi.chat(agentType, { message, conversationId, context, projectId }),
    onSuccess: (data, variables) => {
      console.log('🔄 Chat mutation success:', {
        data,
        variables,
        newConversation: data.newConversation,
        conversationId: data.conversationId
      });
      
      // Invalidate conversation cache to get updated messages
      if (variables.conversationId) {
        console.log('🔄 Invalidating existing conversation cache:', variables.conversationId);
        queryClient.invalidateQueries({ queryKey: ['conversations', variables.conversationId] });
      }
      
      // If a new conversation was created, invalidate both the list and the new conversation
      if (data.newConversation && data.conversationId) {
        console.log('🔄 Invalidating caches for new conversation:', data.conversationId);
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({ queryKey: ['conversations', data.conversationId] });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to get response from agent');
    },
  });

  // Get conversations
  const useConversations = (params?: {
    projectId?: string;
    agentType?: string;
    limit?: number;
    offset?: number;
  }) => {
    return useQuery({
      queryKey: ['conversations', params],
      queryFn: () => agentsApi.getConversations(params),
      enabled: !authLoading && !!user,
      staleTime: 1 * 60 * 1000, // 1 minute
    });
  };

  // Get conversation messages
  const useConversation = (conversationId: string, params?: {
    limit?: number;
    offset?: number;
  }) => {
    return useQuery({
      queryKey: ['conversations', conversationId, params],
      queryFn: () => agentsApi.getConversation(conversationId, params),
      enabled: !authLoading && !!user && !!conversationId,
      staleTime: 1 * 60 * 1000, // 1 minute
    });
  };

  // Archive conversation mutation
  const archiveConversationMutation = useMutation({
    mutationFn: agentsApi.archiveConversation,
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation archived');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to archive conversation');
    },
  });

  // Delete conversation mutation
  const deleteConversationMutation = useMutation({
    mutationFn: agentsApi.deleteConversation,
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete conversation');
    },
  });

  // Get usage stats
  const useUsageStats = (period?: string) => {
    return useQuery({
      queryKey: ['agents', 'usage', period],
      queryFn: () => agentsApi.getUsageStats({ period }),
      enabled: !authLoading && !!user,
      staleTime: 1 * 60 * 60 * 1000, // 1 hour
    });
  };

  return {
    agents: agents?.agents || [],
    isLoading,
    error,
    getAgent,
    chat: chatMutation.mutateAsync,
    isChatting: chatMutation.isPending,
    useConversations,
    useConversation,
    archiveConversation: archiveConversationMutation.mutateAsync,
    deleteConversation: deleteConversationMutation.mutateAsync,
    useUsageStats,
  };
}